import { useEffect, useRef, useState } from "react";
import { Button, Input, Table, DatePicker, Tag, Select } from "antd";
import { SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import { FooterFilterMobile, LoaderCustom } from "../../components";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import api from "../../config/client.axios.api";
import { useGetStoreOfAccountApi } from "../../hooks";
import * as XLSX from "xlsx";

interface TransactionData {
  requestId: string;
  createdDate: string;
  amount: number;
  bankCode: string;
  bankName: string;
  bankTranTime: string;
  fee: number;
  mapId: string;
  merchantCode: string;
  referenceId: string;
  remark: string;
  requestTime: string;
  status: string;
  vaAcc: string;
  vaName: string;
  storeId: string;
  storeName: string;
}

interface StoreSummaryData {
  storeId: string;
  storeName: string;
  totalAmount: number;
  totalTransaction: number;
}

const TransactionManagePage = () => {
  const didInitialFetchRef = useRef(false);
  const { allStoreOfAccount, isLoading: isLoadingStores } =
    useGetStoreOfAccountApi();
  const [searchFilters, setSearchFilters] = useState({
    startDate: "",
    endDate: "",
    storeId: "",
    mapId: "",
  });

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<TransactionData[]>([]);
  const [storeSummarySource, setStoreSummarySource] = useState<
    StoreSummaryData[]
  >([]);
  const [storeSummaryPage, setStoreSummaryPage] = useState(1);
  const [storeSummaryPageSize, setStoreSummaryPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [shouldSearch, setShouldSearch] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  
  useEffect(() => {
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;
    handleSearch();
  }, []);

  useEffect(() => {
    if (shouldSearch) {
      handleSearch();
    }
  }, [page, pageSize]);

  const handleSearch = async () => {
    const params: any = {};
    if (searchFilters.startDate) params.startDate = searchFilters.startDate;
    if (searchFilters.endDate) params.endDate = searchFilters.endDate;
    if (searchFilters.storeId) params.storeId = searchFilters.storeId;
    if (searchFilters.mapId) params.mapId = searchFilters.mapId;
    if (page) params.page = page;
    if (pageSize) params.size = pageSize;

    setLoading(true);
    setShouldSearch(true);
    
    try {
      const response = await api.get('/history/get-payment-history', { params });
      
      const apiData = response.data?.data;

      if (apiData?.storeSummaries && Array.isArray(apiData.storeSummaries)) {
        setStoreSummarySource(apiData.storeSummaries);
        setStoreSummaryPage(1);
      } else {
        setStoreSummarySource([]);
        setStoreSummaryPage(1);
      }
      
      if (apiData?.transactions && Array.isArray(apiData.transactions)) {
        setDataSource(apiData.transactions);
        setTotalRecords(apiData.totalTransaction || apiData.transactions.length);
        setTotalAmount(apiData.totalAmount || 0);
        toast.success(`Tìm thấy ${apiData.transactions.length} giao dịch`);
      } else {
        setDataSource([]);
        setTotalRecords(0);
        setTotalAmount(0);
        toast("Không tìm thấy dữ liệu");
      }
      
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      toast.error(error?.response?.data?.message || "Tìm kiếm thất bại");
    }
  };

  const handleExportExcel = () => {
    if (!storeSummarySource || storeSummarySource.length === 0) {
      toast("Không có dữ liệu tổng hợp để xuất");
      return;
    }

    const exportRows = storeSummarySource.map((row, idx) => ({
      STT: idx + 1,
      "STORE ID": row.storeId,
      "TÊN CỬA HÀNG": row.storeName,
      "TỔNG GIAO DỊCH": row.totalTransaction ?? 0,
      "TỔNG TIỀN (VND)": row.totalAmount ?? 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows, {
      header: [
        "STT",
        "STORE ID",
        "TÊN CỬA HÀNG",
        "TỔNG GIAO DỊCH",
        "TỔNG TIỀN (VND)",
      ],
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Store Summaries");

    const start = searchFilters.startDate || "all";
    const end = searchFilters.endDate || "all";
    const fileName = `store_summaries_${start}_${end}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const storeSummaryColumns = [
    {
      title: "Store ID",
      dataIndex: "storeId",
      key: "storeId",
      width: 140,
      fixed: "left" as const,
    },
    {
      title: "Tên cửa hàng",
      dataIndex: "storeName",
      key: "storeName",
      width: 240,
    },
    {
      title: "Tổng giao dịch",
      dataIndex: "totalTransaction",
      key: "totalTransaction",
      width: 140,
      render: (value: number) => (value ?? 0).toLocaleString("vi-VN"),
    },
    {
      title: "Tổng tiền",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 160,
      render: (value: number) =>
        (value ?? 0).toLocaleString("vi-VN") + " VND",
    },
  ];

  const columns = [
    {
      title: "Request ID",
      dataIndex: "requestId",
      key: "requestId",
      width: 180,
      fixed: "left" as const,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => {
        const color = status === "SUCCESS" ? "green" : status === "FAILED" ? "red" : "orange";
        const text = status === "SUCCESS" ? "Thành công" : status === "FAILED" ? "Thất bại" : "Đang xử lý";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Merchant Code",
      dataIndex: "merchantCode",
      key: "merchantCode",
      width: 140,
    },
    {
      title: "MapId",
      dataIndex: "mapId",
      key: "mapId",
      width: 150,
    },
    {
      title: "Số tài khoản",
      dataIndex: "vaAcc",
      key: "vaAcc",
      width: 160,
    },
    {
      title: "Tên tài khoản",
      dataIndex: "vaName",
      key: "vaName",
      width: 200,
    },
    {
      title: "Mã ngân hàng",
      dataIndex: "bankCode",
      key: "bankCode",
      width: 120,
    },
    {
      title: "Tên ngân hàng",
      dataIndex: "bankName",
      key: "bankName",
      width: 180,
    },
    {
      title: "Số tiền",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      render: (value: number) => {
        return value ? value.toLocaleString("vi-VN") + " VND" : "0 VND";
      },
    },
    {
      title: "Phí",
      dataIndex: "fee",
      key: "fee",
      width: 100,
      render: (value: number) => {
        return value ? value.toLocaleString("vi-VN") + " VND" : "0 VND";
      },
    },
    // {
    //   title: "Store ID",
    //   dataIndex: "storeId",
    //   key: "storeId",
    //   width: 120,
    // },
    {
      title: "Tên cửa hàng",
      dataIndex: "storeName",
      key: "storeName",
      width: 180,
    },
    {
      title: "Thời gian GD ngân hàng",
      dataIndex: "bankTranTime",
      key: "bankTranTime",
      width: 160,
    },
    {
      title: "Thời gian yêu cầu",
      dataIndex: "requestTime",
      key: "requestTime",
      width: 160,
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 160,
      render: (date: string) => {
        return date ? dayjs(date).format("DD/MM/YYYY HH:mm:ss") : "";
      },
    },
    {
      title: "Reference ID",
      dataIndex: "referenceId",
      key: "referenceId",
      width: 300,
    },
    {
      title: "Nội dung",
      dataIndex: "remark",
      key: "remark",
      width: 350,
    },
  ];

  return (
    <>
      <FooterFilterMobile />
      <div className="w-full min-h-screen bg-white p-4 md:p-6">
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6">
            Manage Transactions
          </h2>

          {/* Search Form */}
          <div className="bg-gray-50 p-4 md:p-6 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ngày bắt đầu
                </label>
                <DatePicker
                  className="w-full"
                  format="YYYY-MM-DD"
                  placeholder="Chọn ngày bắt đầu"
                  onChange={(date) => {
                    if (date) {
                      setSearchFilters((prev) => ({
                        ...prev,
                        startDate: dayjs(date).format("YYYY-MM-DD"),
                      }));
                    } else {
                      setSearchFilters((prev) => ({
                        ...prev,
                        startDate: "",
                      }));
                    }
                  }}
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ngày kết thúc
                </label>
                <DatePicker
                  className="w-full"
                  format="YYYY-MM-DD"
                  placeholder="Chọn ngày kết thúc"
                  onChange={(date) => {
                    if (date) {
                      setSearchFilters((prev) => ({
                        ...prev,
                        endDate: dayjs(date).format("YYYY-MM-DD"),
                      }));
                    } else {
                      setSearchFilters((prev) => ({
                        ...prev,
                        endDate: "",
                      }));
                    }
                  }}
                />
              </div>

              {/* Store ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cửa hàng
                </label>
                <Select
                  className="w-full"
                  placeholder="Chọn cửa hàng"
                  showSearch
                  allowClear
                  loading={isLoadingStores}
                  value={searchFilters.storeId || undefined}
                  options={(allStoreOfAccount?.data ?? allStoreOfAccount ?? []).map(
                    (s: any) => ({
                      label: s?.groupName ?? s?.storeName ?? s?.name ?? s?.groupId,
                      value: String(s?.groupId ?? s?.id ?? ""),
                    })
                  )}
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value) => handleInputChange("storeId", value || "")}
                />
              </div>

              {/* MapId */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  mapId
                </label>
                <Input
                  placeholder="mapId"
                  value={searchFilters.mapId}
                  onChange={(e) => handleInputChange("mapId", e.target.value)}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col md:flex-row gap-3 mt-6">
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                className="bg-blue-500 hover:bg-blue-600"
              >
                Tìm kiếm
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                Xuất excel
              </Button>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Có tổng số{" "}
            <span className="font-semibold text-blue-600">{totalRecords}</span>{" "}
            giao dịch. Với tổng số tiền là{" "}
            <span className="font-semibold text-red-600">
              {totalAmount.toLocaleString("vi-VN")} VND
            </span>
          </div>
        </div>

        {/* Store summary table */}
        <div className="mb-6 rounded-lg border border-pink-200 bg-pink-50/40 p-3 md:p-4 shadow-sm">
          <div className="mb-3 flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
            <div className="font-semibold text-gray-900">
              Tổng hợp theo cửa hàng
            </div>
            <div className="text-xs text-gray-600">
              Tổng cửa hàng:{" "}
              <span className="font-semibold text-pink-700">
                {storeSummarySource.length.toLocaleString("vi-VN")}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table
              bordered
              columns={storeSummaryColumns}
              dataSource={storeSummarySource}
              loading={loading}
              rowKey="storeId"
              pagination={{
                current: storeSummaryPage,
                pageSize: storeSummaryPageSize,
                total: storeSummarySource.length,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} của ${total} bản ghi`,
                onChange: (page, pageSize) => {
                  setStoreSummaryPage(page);
                  setStoreSummaryPageSize(pageSize);
                },
              }}
              size="middle"
              scroll={{ x: 760 }}
              locale={{
                emptyText: "Không tìm thấy dữ liệu",
              }}
              summary={(pageData) => {
                const totalTx = pageData.reduce(
                  (acc, row: any) => acc + (Number(row?.totalTransaction) || 0),
                  0
                );
                const totalAmt = pageData.reduce(
                  (acc, row: any) => acc + (Number(row?.totalAmount) || 0),
                  0
                );
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} />
                      <Table.Summary.Cell index={1}>
                        <span className="font-semibold text-gray-900">Tổng</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        <span className="font-semibold text-pink-700">
                          {totalTx.toLocaleString("vi-VN")}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <span className="font-semibold text-pink-700">
                          {totalAmt.toLocaleString("vi-VN")} VND
                        </span>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-purple-300 bg-purple-100/30 p-3 md:p-4 shadow-sm">
          <div className="mb-3 flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
            <div className="font-semibold text-gray-900">Danh sách giao dịch</div>
            <div className="text-xs text-gray-700">
              Tổng giao dịch:{" "}
              <span className="font-semibold text-purple-800">
                {totalRecords.toLocaleString("vi-VN")}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table
              bordered
              columns={columns}
              dataSource={dataSource}
              loading={loading}
              rowKey="requestId"
              pagination={{
                current: page,
                pageSize: pageSize,
                total: totalRecords,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} của ${total} bản ghi`,
                onChange: (page, pageSize) => {
                  setPage(page);
                  setPageSize(pageSize);
                },
              }}
              scroll={{ x: 2800 }}
              locale={{
                emptyText: "Không tìm thấy dữ liệu",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionManagePage;
